import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  SimpleChanges,
} from '@angular/core';
import { v4 } from 'uuid';
import * as _ from 'lodash';
import { CommonModule } from '@angular/common';
import { InputComponent } from '../input/input.component';
import { NgIcon } from '@ng-icons/core';
import { FilterOption } from '../table/table.interface';
import { ReactiveFormsModule } from '@angular/forms';
import { DictionaryService } from '../../services/dictionary.service';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  imports: [CommonModule, InputComponent, NgIcon, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent implements OnChanges, OnInit {
  // Injecting services
  private elementRef = inject(ElementRef<HTMLDivElement>);
  private crf = inject(ChangeDetectorRef);
  dictionaryService = inject(DictionaryService);

  // Inputs
  options = input<string[]>([]);
  optionsIds = input<string[]>([]);
  type = input<'select' | 'check'>('select');
  isFullWidth = input<boolean>(false);
  width = input<string>('min(300px, 100%)');
  size = input<'rg' | 'sm'>('rg');
  name = input<string | undefined>();
  disableSearch = input<boolean>(false);
  defaultOption = input<string | undefined>();
  class = input<string>('');
  placeholder = input<string>(this.dictionaryService.get('SelectAnOption'));
  allUncheckedInitially = input<boolean>(false);

  // Outputs selected option
  getSelectedOption = output<FilterOption>();
  getCheckedItems = output<string[]>();
  getCheckedItemsIds = output<string[]>();

  optionsControlList: FilterOption[] = [];
  selectedOption: FilterOption | undefined | null;
  checkedCount: number = this.options.length;
  filteredOptions: FilterOption[] = [];
  selectId: string = 'select-' + v4();

  allChecked = true;
  lastSearchQuery = '';

  /**
   * Toggles Dropdown Menu
   */
  toggle = () => {
    this.elementRef.nativeElement
      .querySelector(`#${this.selectId}`)
      ?.classList.toggle('expanded');

    this.elementRef.nativeElement
      .querySelector('#toggle-button')
      ?.setAttribute(
        'aria-expanded',
        this.elementRef.nativeElement
          .querySelector('#toggle-button')
          ?.getAttribute('aria-expanded') === 'true'
          ? 'false'
          : 'true',
      );
  };

  /**
   * !! Select Mode Only - Used from outside the component to set select value (all options should be unique)
   * Sets Selected Option State
   */
  updateSelectedOption = (option: string) => {
    const nextOption = this.optionsControlList.find((o) => o.value === option);
    this.selectedOption = nextOption;
    this.crf.detectChanges();
  };

  /**
   * !! Select Mode Only
   * Sets Selected Option State
   */
  setSelectedOption = (optionId: string) => {
    const option = this.optionsControlList.find(
      (option) => option.id === optionId,
    );
    this.selectedOption = option;
    if (option) {
      this.getSelectedOption.emit(option);
    }
    this.toggle();
    this.crf.detectChanges();
  };

  /**
   * !! Check Mode Only
   * Checks/Unchecks a value
   */
  checkOption = (
    optionId: string,
    mode?: 'all' | 'none' | undefined,
    emit: boolean = true,
  ) => {
    if (!mode) {
      const optionIndex = this.optionsControlList.findIndex(
        (option) => option.id == optionId,
      );

      this.optionsControlList[optionIndex].checked =
        !this.optionsControlList[optionIndex].checked;
    } else {
      if (mode === 'all') {
        this.optionsControlList = this.optionsControlList.map((option) => ({
          ...option,
          checked: true,
        }));
      } else if (mode === 'none') {
        this.optionsControlList = this.optionsControlList.map((option) => ({
          ...option,
          checked: false,
        }));
      }
    }

    if (emit) {
      this.getCheckedItems.emit(
        this.optionsControlList
          .filter((option) => option.checked)
          .map((option) => option.value),
      );

      this.getCheckedItemsIds.emit(
        this.optionsControlList
          .filter((option) => option.checked)
          .map((option) => option.id),
      );
    }

    this.filterOptions(this.lastSearchQuery);

    this.checkedCount = this.optionsControlList.filter(
      (option) => option.checked,
    ).length;

    this.allChecked = this.checkedCount === this.optionsControlList.length;
    this.crf.detectChanges();
  };

  checkAll = () => {
    if (this.checkedCount < this.optionsControlList.length) {
      this.checkOption('', 'all');
    } else {
      this.checkOption('', 'none');
    }

    this.crf.detectChanges();
  };

  /**
   * Triggered on option click
   */
  optionOnClick = (optionId: string) => {
    if (this.type() === 'select') {
      this.setSelectedOption(optionId);
    } else {
      this.checkOption(optionId);
    }
  };

  /**
   * Filters the options based on search query
   */
  filterOptions = (query: string) => {
    this.lastSearchQuery = query;
    this.filteredOptions = [
      ...this.optionsControlList.filter((option) =>
        option.value.toLowerCase().includes(query.toLowerCase()),
      ),
    ];
  };

  optionTrackBy = (index: number, option: FilterOption) => {
    return option.id + String(option.checked);
  };

  setInitialSelection = () => {
    if (this.defaultOption()) {
      const foundOption = this.optionsControlList.find(
        (option) => option.value === this.defaultOption(),
      );

      if (foundOption) {
        this.selectedOption = foundOption;
      } else {
        this.selectedOption = this.optionsControlList[0];
      }
    } else {
      this.selectedOption = null;
    }
  };

  ngOnInit(): void {
    const optionsControlList: FilterOption[] = [];
    this.options().forEach((option, index) => {
      optionsControlList.push({
        value: option,
        id: this.optionsIds().length ? this.optionsIds()[index] : v4(),
        [this.type() === 'check' ? 'checked' : 'selected']:
          this.allUncheckedInitially() ? false : true,
      });
    });

    this.optionsControlList = [...optionsControlList];

    this.setInitialSelection();
    this.checkedCount = this.allUncheckedInitially()
      ? 0
      : this.optionsControlList.length;
    this.allChecked = this.checkedCount === this.optionsControlList.length;
    this.filteredOptions = [...this.optionsControlList];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultOption'] && !changes['defaultOption'].firstChange) {
      this.setInitialSelection();
      this.crf.detectChanges();
    }

    if (!changes['options']) {
      return;
    }

    const changed = !_.isEqual(
      changes['options'].currentValue,
      changes['options'].previousValue,
    );

    if (changed) {
      if (this.type() === 'check') {
        const optionsControlList: FilterOption[] = [];

        this.options().forEach((option, index) => {
          optionsControlList.push({
            value: option,
            id: this.optionsIds().length ? this.optionsIds()[index] : v4(),
            checked: this.allUncheckedInitially() ? false : true,
          });
        });

        this.optionsControlList = [...optionsControlList];

        this.checkedCount = this.allUncheckedInitially()
          ? 0
          : this.optionsControlList.length;

        this.allChecked = this.checkedCount === this.optionsControlList.length;
      } else {
        const optionsControlList: FilterOption[] = [];
        this.options().forEach((option, index) => {
          optionsControlList.push({
            value: option,
            id: this.optionsIds().length ? this.optionsIds()[index] : v4(),
            selected: this.allUncheckedInitially() ? false : true,
          });
        });

        this.optionsControlList = [...optionsControlList];
      }

      // First Option is selected by default - !! Select Mode
      this.setInitialSelection();
      this.filteredOptions = [...this.optionsControlList];

      this.crf.detectChanges();
    }
  }
}
